import { AnalyticsProvider } from "@asurion-core/eventualize-react";
import { useDefaultMessages } from "@expert/contentful";
import { FeaturesProvider, type TimelineContextType, type TimelineFeatures, useFlags } from "@expert/features";
import { Stack } from "@mantine/core";
import { forwardRef, type JSX, useEffect, useMemo, useRef } from "react";

import type { ExpertAssistMessage, TimelineProps } from "../../shared-types";

import { useGaiaAutoCompleteEvents } from "../../hooks";
import { fallbackDefaultMessages, useTimelineStore } from "../../state";
import { useAutoscroll } from "../../utils";
import { MessageInput } from "../message-input";
import { TimelineList } from "../timeline-list";

export const Timeline = forwardRef<HTMLDivElement, TimelineProps>(function Timeline(
    {
        callSid,
        chatId,
        defaultFeatures,
        expertId,
        expertName,
        featuresContext: context,
        toolboxContext = {},
        lob,
        partner,
        logger: loggerProp,
        sessionId,
        SubscriptionsWithFeatures,
        gaiaPayloadProps,
        IssueSummaryComponent,
    }: TimelineProps,
    ref,
): JSX.Element {
    const { setExpert, setTimelineProperties, setDefaultMessages, getTimelineKey } = useTimelineStore();
    const logger = useMemo(() => loggerProp.child({ module: "Timeline", partner }), [loggerProp, partner]);

    const scrollRef = useRef<HTMLDivElement>(null);
    const { start: startAutoscroll } = useAutoscroll(scrollRef);

    const contentfulDefaultMessages = useDefaultMessages(fallbackDefaultMessages) as ExpertAssistMessage[];
    useGaiaAutoCompleteEvents(logger);

    const features = useFlags<TimelineContextType>({
        analyticGroup: "Timeline",
        context,
        features: defaultFeatures,
        logger: loggerProp,
    }) as TimelineFeatures;

    useEffect(() => {
        logger.info({ expertId, expertName }, "Timeline Setup | expert");
        setExpert({ expertId, expertName });
        // oxlint-disable-next-line exhaustive-deps
    }, [setExpert, expertId, expertName]);

    useEffect(() => {
        // Set the current session ID in the timeline store each time the sessionId changes
        useTimelineStore.getState().setCurrentSessionId(sessionId);
    }, [sessionId]);

    useEffect(() => {
        const timeline = useTimelineStore.getState().getTimelineState(sessionId);
        if (
            timeline.partner !== partner ||
            timeline.callSid !== callSid ||
            timeline.lob !== lob ||
            timeline.chatId !== chatId
        ) {
            logger.info({ partner, sessionId, callSid, lob, chatId }, "Timeline Setup | timeline meta");
            setTimelineProperties(sessionId, { partner, callSid, lob, chatId });
        }
        // oxlint-disable-next-line exhaustive-deps
    }, [setTimelineProperties, partner, sessionId, callSid, lob, chatId]);

    useEffect(() => {
        setDefaultMessages(contentfulDefaultMessages);
    }, [setDefaultMessages, contentfulDefaultMessages]);

    useEffect(() => {
        if (callSid || chatId) {
            startAutoscroll(); // auto scroll for a new call
        }
    }, [callSid, chatId, startAutoscroll]);

    return (
        <AnalyticsProvider
            dispatcher={(analyticsDispatcher) =>
                analyticsDispatcher.withIdentities({ ExpertAssistSessionId: sessionId })
            }
        >
            <Stack
                align="center"
                data-testid="timeline-wrapper"
                justify="flex-end"
                flex="1"
                style={{ overflow: "hidden" }}
                ref={ref}
                w="100%"
                mih="10rem"
            >
                <FeaturesProvider features={features}>
                    {/*
                        some of the subscriptions specific to the workspace and standalone need to be able
                        to access the features context, so we have to pass the subscriptions down as a prop
                        and call it under the features provider

                        be sure to REFACTOR this if the subscriptions do not need the features in the future.
                    */}
                    {SubscriptionsWithFeatures}
                    <TimelineList
                        logger={logger}
                        scrollRef={scrollRef}
                        gaiaPayloadProps={gaiaPayloadProps}
                        IssueSummaryComponent={IssueSummaryComponent}
                    />
                    <MessageInput
                        logger={logger}
                        scrollRef={scrollRef}
                        toolboxContext={toolboxContext}
                        gaiaPayloadProps={gaiaPayloadProps}
                        key={getTimelineKey(sessionId)}
                    />
                </FeaturesProvider>
            </Stack>
        </AnalyticsProvider>
    );
});
